import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/api";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 px-5 py-4 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-brand-yellow/60 focus:ring-2 focus:ring-brand-yellow/20 transition-all duration-300";

function DriverForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type: "driver", ...form });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong", {
        description: err?.response?.data?.detail || "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {done ? (
        <motion.div
          key="success"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex flex-col items-start gap-4 py-10"
          data-testid="driver-success-message"
        >
          <CheckCircle2 className="w-12 h-12 text-brand-green" />
          <h3 className="font-display font-semibold text-3xl tracking-tight">You're in the driver's seat.</h3>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
            Thanks, {form.name.split(" ")[0] || "driver"}. We'll reach out at {form.email} with
            your onboarding steps before launch day.
          </p>
          <button
            onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "" }); }}
            className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors duration-300"
            data-testid="driver-reset-button"
          >
            Add another driver
          </button>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          onSubmit={submit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
          data-testid="driver-signup-form"
        >
          <input
            required
            type="text"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            data-testid="driver-name-input"
          />
          <input
            required
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
            data-testid="driver-email-input"
          />
          <input
            required
            type="tel"
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={inputCls}
            data-testid="driver-phone-input"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="driver-submit-button"
            className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-yellow text-black font-bold px-8 py-4 hover:bg-brand-yellow-hover hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 transition-all duration-300"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                Start earning today
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>
          <p className="text-xs text-zinc-500 leading-relaxed">
            No commitment. We'll only contact you about driving with EzRueda in Albany.
          </p>
        </motion.form>
      )}
    </AnimatePresence>
  );
}

function RiderForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type: "rider", email });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong", {
        description: err?.response?.data?.detail || "Please check your email and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {done ? (
        <motion.div
          key="success"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex flex-col items-start gap-4 py-10"
          data-testid="rider-success-message"
        >
          <CheckCircle2 className="w-12 h-12 text-brand-yellow" />
          <h3 className="font-display font-semibold text-3xl tracking-tight">First ride, on its way.</h3>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
            We'll send your launch invite and first-ride offer to {email} the moment EzRueda
            goes live in Albany.
          </p>
          <button
            onClick={() => { setDone(false); setEmail(""); }}
            className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors duration-300"
            data-testid="rider-reset-button"
          >
            Use a different email
          </button>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          onSubmit={submit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
          data-testid="rider-signup-form"
        >
          <input
            required
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputCls} focus:border-brand-green/60 focus:ring-brand-green/20`}
            data-testid="rider-email-input"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="rider-submit-button"
            className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-green text-black font-bold px-8 py-4 hover:bg-brand-green-hover hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 transition-all duration-300"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                Get your first ride
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Launch invite plus a first-ride offer. No spam, ever.
          </p>
        </motion.form>
      )}
    </AnimatePresence>
  );
}

export default function SignupForms() {
  return (
    <section className="relative py-28 md:py-36 overflow-hidden" data-testid="signup-section">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-brand-yellow/8 rounded-full blur-[160px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 md:mb-24 text-left"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-5">
            Join the launch
          </p>
          <h2 className="font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Be first on the road <span className="text-zinc-500">in Albany.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            id="driver-signup"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="rounded-3xl border border-white/10 bg-brand-surface p-8 md:p-12 scroll-mt-28 hover:border-brand-yellow/40 transition-colors duration-500"
            data-testid="driver-signup-panel"
          >
            <span className="inline-block rounded-full bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 mb-8">
              Drivers
            </span>
            <h3 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-3">
              Start earning today.
            </h3>
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-10 max-w-md">
              Leave your details and we'll fast-track your application before launch. Highest
              driver take-home in the Capital Region.
            </p>
            <DriverForm />
          </motion.div>

          <motion.div
            id="rider-signup"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.12 }}
            className="rounded-3xl border border-white/10 bg-brand-surface p-8 md:p-12 scroll-mt-28 hover:border-brand-green/40 transition-colors duration-500"
            data-testid="rider-signup-panel"
          >
            <span className="inline-block rounded-full bg-brand-green/10 border border-brand-green/30 text-brand-green text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 mb-8">
              Riders
            </span>
            <h3 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-3">
              Get your first ride.
            </h3>
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-10 max-w-md">
              Drop your email for a launch invite and an exclusive first-ride offer. Cheaper
              trips across Albany from day one.
            </p>
            <RiderForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
